import { RiFileExcel2Line } from "react-icons/ri";
import { BsShop } from "react-icons/bs";
import { FiX } from "react-icons/fi";
import { useState, useMemo, useCallback, useEffect } from "react";

import {
  Button,
  DateInput,
  DropDownInput,
  InputField,
  RadioInput,
  MiniTabs,
  DataTable,
  OptionGroup,
} from "@/components";
import BookedOrderForm from "./BookedOrderForm";
import ColumnHelper from "@/utils/ColumnHelper";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog as ErrorDialog,
  AlertDialogContent as ErrorDialogContent,
  AlertDialogHeader as ErrorDialogHeader,
  AlertDialogTitle as ErrorDialogTitle,
  AlertDialogDescription as ErrorDialogDescription,
  AlertDialog as DuplicateDialog,
  AlertDialogContent as DuplicateDialogContent,
  AlertDialogHeader as DuplicateDialogHeader,
  AlertDialogTitle as DuplicateDialogTitle,
  AlertDialogDescription as DuplicateDialogDescription,
} from "@/components/ui/alert-dialog";

import {
  useFilter,
  useAlertDialog,
  useAlert,
  useConfirmationDialog,
  useValidation,
} from "@/";
import { SpaceWrapper } from "../../components";
import { useSelector } from "react-redux";
import { BiSolidEditAlt, BiSolidTruck } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { ImportOrdersFromChannel, selectAllOrders } from "../../app/slices";
import { cancelOrder, bookOrders, createOrder } from "../../app/slices";
import { useDispatch } from "react-redux";
import { subDays, format } from "date-fns";
import { fetchOrderData } from "../../app/slices";
import { IoInformation } from "react-icons/io5";
import { CreateOrderSchema } from "../../schemas";
import { Copy } from "lucide-react";

const BookedOrder = () => {
  // General state
  const [activeTab, setActiveTab] = useState("all");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const { showDialog, AlertDialog } = useAlertDialog();
  const { confirm, ConfirmationModal } = useConfirmationDialog();
  const { alert, AlertDialog: Dialog } = useAlert();
  const [orderResponse, setOrderResponse] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [selectedErrors, setSelectedErrors] = useState([]);

  // Duplicate order dialog state
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicatePendingData, setDuplicatePendingData] = useState(null);
  const [duplicateOrderId, setDuplicateOrderId] = useState("");
  const [duplicateInvoiceNo, setDuplicateInvoiceNo] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { validate } = useValidation(CreateOrderSchema);
  const dateRange =
    useSelector((state) => state.dashboard?.data?.dateRange) || {};
  const user = useSelector((state) => state.user.user);
  // const [order, setOrder] = useState(null);
  const currentDate = new Date();
  const startOfMonthDate = dateRange?.startDate
    ? new Date(dateRange.startDate)
    : subDays(currentDate, 30); // Last 30 days
  const endOfMonthDate = dateRange?.endDate
    ? new Date(dateRange.endDate)
    : currentDate;

  // Initial filters
  const [filters, setFilters] = useState({
    orderId: "",
    productName: "",
    channelName: "",
    paymentMode: "",
    date: { from: startOfMonthDate, to: endOfMonthDate },
  });

  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [resetPageCounter, setResetPageCounter] = useState(0);

  // General filter configuration.
  const filterConfig = useMemo(
    () => ({
      orderId: {
        type: "comma-api",
        itemKey: "orderId",
        model: "Order",
        endpoint: "/common/search",
      },
      productName: {
        type: "partial",
        itemKey: "products.productName",
      },
      channelName: { type: "exact", itemKey: "channel" },
      paymentMode: { type: "exact", itemKey: "paymentMode" },
      date: { type: "date", itemKey: "createdAt" },
    }),
    [],
  );

  // Tab filter configuration as a serializable object.
  const tabConfig = useMemo(
    () => ({
      idKey: "status",
      allTabId: "all",
      filters: {
        booked: { type: "exact", value: "Booked" },
        notBooked: { type: "exact", value: "Not Booked" },
        // Additional tabs can be added here.
      },
    }),
    [],
  );

  const fetchData = async () => {
    const response = await dispatch(
      fetchOrderData({
        startDate: filters.date.from,
        endDate: filters.date.to,
      }),
    );
    console.log("response::::::", response);
  };

  useEffect(() => {
    fetchData();
  }, [appliedFilters.date, dispatch]);

  const orders = useSelector(selectAllOrders) || [];
  const apiLoading = useSelector((state) => state.order.loading) || false;
  const channel = useSelector((state) => state.app.appData?.channels) || [];
  const courier = useSelector((state) => state.app?.appData?.courier) || [];

  const channelOptions = useMemo(
    () => channel.map((c) => ({ value: c.name, label: c.name })),
    [channel],
  );
  // Use the combined asynchronous filtering hook.
  const {
    data: finalFilteredData,
    counts,
    isLoading,
  } = useFilter(orders, appliedFilters, filterConfig, activeTab, tabConfig);

  // Format currency values
  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(value);
  }, []);

  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  const getCourierName = useCallback(
    (courierId) => {
      const courierInfo = courier.find((c) => c.id === courierId);
      return courierInfo?.name || courierId;
    },
    [courier],
  );

  const handleEdit = useCallback((order) => {
    console.log("Edit Order:", order);
    if (
      order.exceptionMessage.some(
        (exception) => exception.code !== "COURIER_NOT_AVAILABLE",
      )
    ) {
      console.log("Other errors exist");
      navigate(`/newBooking`, { state: { orderData: order, isEdit: true } });
    } else {
      console.log("Only courier error exists");
    }
  }, []);

  const handleView = useCallback((order) => {
    console.log("View Order:", order);
    navigate(`/newBooking`, {
      state: { orderData: order, isEdit: true, isView: true },
    });
  }, []);
  const generateDuplicateCode = (value) => {
    const now = new Date();
    const datePart = format(now, "dd/MM/yy");
    const timePart = format(now, "HH:mm:ss");
    return `${value}COPY-${datePart}-${timePart}`;
  };

  // Open dialog so user can review/edit the generated IDs before confirming
  const handleDuplicate = (data) => {
    const generatedOrderId = generateDuplicateCode(data.orderId);
    const generatedInvoiceNo = generateDuplicateCode(
      data.invoice.invoiceNumber,
    );
    setDuplicatePendingData(data);
    setDuplicateOrderId(generatedOrderId);
    setDuplicateInvoiceNo(generatedInvoiceNo);
    setDuplicateDialogOpen(true);
  };

  // Called when user confirms in the dialog
  const confirmDuplicate = async () => {
    const data = duplicatePendingData;
    if (!data) return;

    const duplicatedOrder = {
      orderId: duplicateOrderId.trim(),
      orderDate: new Date().toISOString(),

      paymentMode: data.paymentMode,

      shipmentId: "",
      hasShipmentId: false,

      deliveryAddress: {
        ...data.deliveryAddress,
        gstNumber: data.deliveryAddress.gstNumber || "",
      },

      deliveryPhones: data.deliveryPhones.map((p) => ({
        phoneType: p.phoneType,
        phoneNumber: p.phoneNumber,
      })),

      invoice: {
        invoiceDate: new Date().toISOString(),
        invoiceNumber: duplicateInvoiceNo.trim(),
        discount: data.invoice.discount,
        shippingCharge: data.invoice.shippingCharge,
        hasShippingCharge: data.invoice.hasShippingCharge,
        totalAmount: data.invoice.totalAmount,
      },

      ebn: {
        hasEBN: data.ebn.hasEBN,
        ebnNumber: data.ebn.ebnNumber || "",
        ebnExpiryDate: data.ebn.ebnExpiryDate || "",
      },

      physicalWeight: data.physicalWeight,
      dimensions: {
        length: data.dimensions.length,
        breadth: data.dimensions.breadth,
        height: data.dimensions.height,
      },

      products: data.products.map((p) => {
        const price = Number(p.orderValue) > 0 ? Number(p.orderValue) : 1;
        const quantity = Number(p.quantity) > 0 ? Number(p.quantity) : 1;
        return {
          productName: p.productName || p.name || "",
          price,
          quantity,
          orderValue: price,
          sku: p.sku || "",
          hsn: p.hsn || "",
        };
      }),

      hasException: false,
      exceptionMessage: [],
    };

    const result = validate(duplicatedOrder);
    if (!result.isValid) {
      console.error("Duplicate Validation Failed:", result.errors);
      alert("Duplicate Failed: Fix validation issues.");
      return;
    }

    setDuplicateDialogOpen(false);

    try {
      const action = await dispatch(createOrder(result.data));
      if (createOrder.fulfilled.match(action)) {
        alert("Duplicate Order Created Successfully");
      } else {
        alert("Failed: " + (action.payload || "Unknown error"));
      }
    } catch (err) {
      console.error("Error creating duplicate:", err);
      alert("Error creating duplicate");
    }
  };

  // Table columns - Updated for industry standards with proper tooltip handling
  const columns = useMemo(
    () => [
      ColumnHelper({
        header: "Channel",
        accessorKey: "channel",
        cell: (info) => info.row.original.channel || "N/A",
        meta: {
          tooltip: (row) => row.channelName || "N/A",
        },
      }),
      ColumnHelper({
        header: "Order ID",
        accessorKey: "orderId",
        cell: (info) => (
          <span
            onClick={() => handleView(info.row.original)}
            className="underline text-blue-600 cursor-pointer"
          >
            {info.row.original.orderId}
          </span>
        ),
        meta: {
          tooltip: (row) => row.orderId,
        },
      }),
      ColumnHelper({
        header: "Order Date",
        accessorKey: "orderDate",
        cell: (info) => formatDate(info.row.original.orderDate),
        meta: {
          tooltip: (row) => formatDate(row.orderDate),
        },
      }),
      ColumnHelper({
        header: "Product",
        accessorKey: "products",
        cell: (info) => {
          const products = info.row.original.products
            ?.map((product) => product?.productName)
            .join(",");
          return <span className="">{products}</span>;
        },
        meta: {
          tooltip: (row) => row.products?.map((p) => p.productName).join(", "),
        },
      }),
      ColumnHelper({
        header: "Payment",
        accessorKey: "totalOrderValue",
        cell: (info) => (
          <span className="font-nunito">
            {formatCurrency(info.row.original.totalOrderValue)}
          </span>
        ),
        meta: {
          tooltip: (row) => formatCurrency(row.totalOrderValue),
        },
      }),
      ColumnHelper({
        header: "Collectable Amount",
        accessorKey: "collectableAmount",
        cell: (info) => (
          <span className="font-nunito">
            {formatCurrency(
              info.row.original.paymentMode === "Prepaid"
                ? 0
                : info.row.original.totalOrderValue,
            )}
          </span>
        ),
        meta: {
          tooltip: (row) => formatCurrency(row.totalOrderValue),
        },
      }),
      ColumnHelper({
        header: "Payment Mode",
        accessorKey: "paymentMode",
        meta: {
          tooltip: (row) => row.paymentMode,
        },
      }),
      ColumnHelper({
        header: "Customer",
        accessorKey: "deliveryAddress.customerName",
        cell: (info) => info.row.original.deliveryAddress?.customerName,
        meta: {
          tooltip: (row) => row?.deliveryAddress?.customerName,
        },
      }),
      ColumnHelper({
        header: "Zip Code",
        accessorKey: "deliveryAddress.pincode",
        cell: (info) => info.row.original.deliveryAddress?.pincode,
        meta: {
          tooltip: (row) => row.deliveryAddress?.pincode,
        },
      }),
      ColumnHelper({
        header: "Weight",
        accessorKey: "physicalWeight",
        cell: (info) => `${info.row.original.physicalWeight} kg`,
        meta: {
          tooltip: (row) => `${row.physicalWeight} kg`,
        },
      }),
      ColumnHelper({
        header: "Status",
        accessorKey: "status",
        cell: (info) =>
          info.row.original.status !== "Booked" &&
          info.row.original.status !== "Cancelled" &&
          info.row.original.hasException ? (
            !info.row.original.exceptionMessage.some(
              (exception) =>
                exception.code !== "COURIER_NOT_AVAILABLE" &&
                exception.code !== "EXISTING_EXCEPTION" &&
                exception.code !== "PINCODE_NOT_SERVICEABLE",
            ) ? (
              <div className="flex items-center gap-2">
                <button
                  className={`px-2 py-1 w-fit flex items-center cursor-pointer border-red-800/20 justify-center rounded-md text-xs font-medium leading-3 bg-red-100 text-red-800`}
                >
                  Courier Error
                </button>
                <button
                  onClick={() => {
                    setSelectedErrors(info.row.original.exceptionMessage);
                    setErrorDialogOpen(true);
                  }}
                  className="text-gray-500 hover:text-gray-700 cursor-pointer border border-slate-400 !bg-slate-200 p-0.5 rounded-lg"
                >
                  <IoInformation size={17} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleEdit(info.row.original)}
                  className={`px-2 py-1.5 w-fit flex items-center border cursor-pointer  border-red-800/20 justify-center rounded-md text-xs font-medium leading-3 bg-red-100 text-red-800`}
                >
                  Edit
                  <BiSolidEditAlt className="ml-1 text-red-800 text-xs" />
                </button>
                <button
                  onClick={() => {
                    setSelectedErrors(info.row.original.exceptionMessage);
                    setErrorDialogOpen(true);
                  }}
                  className="text-gray-500 hover:text-gray-700 cursor-pointer border border-slate-400 !bg-slate-200 p-0.5 rounded-lg"
                >
                  <IoInformation size={17} />
                </button>
              </div>
            )
          ) : (
            <span
              className={`px-2 py-1 rounded-md text-xs font-medium leading-3 ${
                info.row.original.status === "Booked"
                  ? "bg-green-100/90 text-green-800"
                  : info.row.original.status === "Cancelled"
                    ? "bg-cyan-700/20 text-cyan-900"
                    : "bg-yellow-100 text-yellow-800"
              }`}
            >
              {info.row.original.status}
            </span>
          ),
        meta: {
          tooltip: (row) =>
            row.status !== "Booked" &&
            row.status !== "Cancelled" &&
            row.hasException
              ? Array.isArray(row.exceptionMessage)
                ? row.exceptionMessage.map((ex) => ex.code).join(", ")
                : row.exceptionMessage
              : row.status,
        },
      }),
      ColumnHelper({
        header: "Actions",
        accessorKey: "_id",
        cell: (info) => (
          <div className="flex gap-1 justify-start items-center text-sm w-[50px]">
            <Button
              size="xs"
              variant="outline"
              onClick={() => handleDuplicate(info.row.original)}
            >
              <Copy size={16} strokeWidth={1} />
            </Button>
          </div>
        ),
        meta: {
          tooltip: () => "Duplicate Order",
        },
      }),
    ],
    [],
  );

  // Build tabs using counts returned by the worker.
  const tabs = useMemo(
    () => [
      { id: "all", label: "All", count: counts.all || 0 },
      { id: "booked", label: "Booked", count: counts.booked || 0 },
      { id: "notBooked", label: "Not Booked", count: counts.notBooked || 0 },
    ],
    [counts],
  );

  // Options for additional actions.
  const options = useMemo(
    () => [
      { label: "Pickup", value: "place_order", icon: <BiSolidTruck /> },
      { label: "Cancel", value: "cancel", icon: <FiX /> },
    ],
    [],
  );

  // Handlers for updating filters.
  const handleChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleDateChange = useCallback((dateRange) => {
    setFilters((prev) => ({
      ...prev,
      date: { from: dateRange?.from || "", to: dateRange?.to || "" },
    }));
  }, []);

  const handleApply = useCallback(() => {
    console.log("Filters applied:", filters);
    setAppliedFilters(filters);
    setResetPageCounter((prev) => prev + 1);
  }, [filters]);

  useEffect(() => {
    console.log(apiLoading);
  }, [apiLoading]);

  const handleReset = useCallback(() => {
    const initialFilters = {
      orderId: "",
      productName: "",
      channelName: "",
      paymentMode: "",
      date: { from: startOfMonthDate, to: endOfMonthDate },
    };
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setResetPageCounter((prev) => prev + 1);
  }, []);

  const handleTabChange = useCallback((tab) => {
    console.log("Tab Changed:", tab);
    setActiveTab(tab);
  }, []);

  const handleOptionClick = useCallback(
    (value) => {
      if (value === "place_order") setSheetOpen(true);
      if (value === "cancel") handleCancelOrder();
    },
    [selectedRows],
  );

  const handelPlaceOrder = async (formData) => {
    console.log("Form Data:", formData);
    console.log("Selected Rows:", selectedRows);
    const orders = selectedRows.map((row) => ({
      _id: row._id,
      orderId: row.orderId,
    }));

    const courierId = formData.courierPartner.split(".")[0];
    const courierName = formData.courierPartner.split(".")[1];
    const mode = formData.mode;
    const pickupAddressId = formData.pickupAddressId;

    const body = {
      courierId,
      mode,
      courierName,
      orders,
      pickupAddressId,
    };

    console.log("Body:", body);

    try {
      const action = await dispatch(bookOrders(body));
      if (bookOrders.fulfilled.match(action)) {
        setSheetOpen(false);
        setOrderResponse(action.payload);
        showDialog();
        console.log("Order Placed Response:", action.payload);
      } else if (bookOrders.rejected.match(action)) {
        setSheetOpen(false);
        console.error("Error placing order:", action.payload);
        alert(`Failed to place order: ${action.payload || "Unknown error"}`);
      }
    } catch (error) {
      setSheetOpen(false);
      console.error("Error saving order:", error);
      alert("An unexpected error occurred while placing the order");
    }
  };

  const handleCancelOrder = async () => {
    // Show confirmation dialog
    const confirmed = await confirm(
      "Are you sure you want to cancel the selected order",
    );
    if (!confirmed) return;

    const orders = selectedRows.map((row) => ({
      _id: row._id,
      orderId: row.orderId,
    }));

    const body = {
      orders,
    };

    try {
      const action = await dispatch(cancelOrder(body));
      if (cancelOrder.fulfilled.match(action)) {
        setSelectedRows([]); // Clear selection after successful cancellation
        alert("Order Cancelled Successfully");
        console.log("Order Cancelling Response:", action.payload);
      } else if (cancelOrder.rejected.match(action)) {
        console.error("Cancellation failed:", action.payload);
        alert(`Failed to cancel order: ${action.payload || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Unexpected error during cancellation:", error);
      alert("An unexpected error occurred while cancelling the order");
    }
  };

  const handleImportFromChannel = async () => {
    if (
      !user.kycStatus ||
      user.kycStatus === "not_submitted" ||
      user.kycStatus === "pending" ||
      user.kycStatus === "rejected"
    ) {
      alert("Please complete your KYC to import orders from channels");
      return;
    }
    try {
      const action = await dispatch(ImportOrdersFromChannel());
      if (ImportOrdersFromChannel.fulfilled.match(action)) {
        if (action.payload.length > 0) {
          alert("Orders imported successfully");
        } else {
          alert("No orders found to import");
        }
        console.log("Orders imported successfully:", action.payload);
      } else if (ImportOrdersFromChannel.rejected.match(action)) {
        alert("Failed to import orders");
        console.error("Error importing orders:", action.payload);
      }
    } catch (error) {
      alert("An unexpected error occurred while importing orders");
      console.error("Unexpected error during import:", error);
    }
  };

  return (
    <SpaceWrapper>
      <div className="w-full flex-1 flex flex-col gap-4 p-4 pb-20 md:pb-4 relative max-w-[2000px]">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row gap-2 justify-between">
          <h2 className="text-headline3 lg:text-headline2 text-secondary/95 font-nunito font-bold">
            Booked Orders
          </h2>
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetContent aria-describedby="">
              <SheetHeader>
                <SheetTitle>Select Courier Partner</SheetTitle>
              </SheetHeader>
              <BookedOrderForm
                setSheetOpen={setSheetOpen}
                handelPlaceOrder={handelPlaceOrder}
                isLoading={apiLoading}
                orders={selectedRows}
              />
            </SheetContent>
          </Sheet>
          <div className="flex gap-2  ">
            {/* <Button variant="default" size="sm" icon={RiFileExcel2Line}>
              Export
            </Button> */}
            <Button
              variant="default"
              size="sm"
              icon={BsShop}
              onClick={handleImportFromChannel}
            >
              Import from Channel
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          <DateInput
            label="Date Filter"
            placeholder="Select start & end date"
            mode="range"
            value={filters.date}
            onChange={handleDateChange}
          />
          <InputField
            label="Order ID"
            placeholder="Enter Order ID separated by commas"
            value={filters.orderId}
            onChange={(val) => handleChange("orderId", val || "")}
          />
          <InputField
            label="Product Name"
            placeholder="Enter Product Name"
            value={filters.productName}
            onChange={(val) => handleChange("productName", val)}
          />
          <DropDownInput
            label="Channel Name"
            placeholder="Select Channel Name"
            options={channelOptions}
            value={filters.channelName}
            onChange={(val) => handleChange("channelName", val)}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          <RadioInput
            label="Payment Mode"
            name="payment_mode"
            options={[
              { value: "Prepaid", label: "Prepaid" },
              { value: "COD", label: "COD" },
            ]}
            selectedValue={filters.paymentMode}
            onChange={(val) => handleChange("paymentMode", val)}
          />
          <div className="flex flex-row gap-2 justify-end md:justify-start items-end">
            <Button
              variant="default"
              size="md"
              onClick={handleApply}
              className="h-12"
            >
              Apply
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={handleReset}
              className="h-12"
            >
              Reset
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <MiniTabs
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={handleTabChange}
        />

        {/* Options */}
        {selectedRows.length > 0 && (
          <OptionGroup
            options={options}
            selectedCount={selectedRows.length}
            onClick={handleOptionClick}
          />
        )}

        {/* DataTable */}
        <DataTable
          columns={columns}
          tittle="Booked Orders"
          data={finalFilteredData}
          getSelectedRows={setSelectedRows}
          resetPage={resetPageCounter}
          isLoading={isLoading}
          actions={[
            {
              name: "Export",
              onClick: () => {},
              icon: RiFileExcel2Line,
              // In the DataTable actions configuration
              body: {
                model: "Order",
                filename: `booked_orders${new Date().getTime()}`,
              },
            },
          ]}
          isRowSelectable={(rowData) => {
            if (rowData.status !== "Not Booked") {
              return false;
            }

            if (!rowData.exceptionMessage.length) {
              return true;
            }

            return !rowData.exceptionMessage.some(
              (exception) =>
                exception.code !== "COURIER_NOT_AVAILABLE" &&
                exception.code !== "EXISTING_EXCEPTION" &&
                exception.code !== "INSUFFICIENT_WALLET_BALANCE" &&
                exception.code !== "OTHER_ERROR" &&
                exception.code !== "PINCODE_NOT_SERVICEABLE",
              rowData.hasException,
            );
          }}
        />
      </div>

      <AlertDialog>
        <div className="!text-headline5 leading-6 font-nunito mb-1.5 text-secondary/90 font-bold">
          Result of Order Placement
        </div>
        <div className="text-body2 leading-[18px] w-full font-nunito text-secondary/80 font-semibold">
          Details about failed orders can be viewed in the Booked Order Table&apos;s
          Status Column
        </div>
        <div className="p-4 bg-background_2/40 rounded-xl my-4 flex flex-col justify-center gap-4">
          <div className="flex justify-center gap-4">
            <div className="p-4 bg-blue-500/15 rounded-2xl h-20 w-20 flex justify-center items-center flex-col">
              <span className="text-blue-700/95 text-subtitle0 font-nunito font-bold">
                {orderResponse?.summary?.totalOrders}
              </span>
              <span className="text-blue-700 text-body2 font-nunito font-semibold">
                Total
              </span>
            </div>
            <div className="p-4 bg-green-500/15 rounded-2xl h-20 w-20 flex justify-center items-center flex-col">
              <span className="text-green-700/95 text-subtitle0 font-nunito font-bold">
                {orderResponse?.summary?.succeeded}
              </span>
              <span className="text-green-700 text-body2 font-nunito font-semibold">
                Placed
              </span>
            </div>
            <div className="p-4 bg-red-500/15 rounded-2xl h-20 w-20 flex justify-center items-center flex-col">
              <span className="text-red-700/95 text-subtitle0 font-nunito font-bold">
                {orderResponse?.summary?.totalOrders -
                  orderResponse?.summary?.succeeded}
              </span>
              <span className="text-red-700 text-body2 font-nunito font-semibold">
                Failed
              </span>
            </div>
          </div>
          <span className="!font-nunito flex justify-center gap-2 font-semibold text-secondary/90">
            Success Rate :{" "}
            <span className="text-green-600/90 font-bold text-subtitle1">
              {orderResponse?.summary?.successRate}
            </span>
          </span>
        </div>
      </AlertDialog>
      <ErrorDialog open={errorDialogOpen} onOpenChange={setErrorDialogOpen}>
        <ErrorDialogContent className="max-w-2xl p-0 gap-0 md:rounded-3xl rounded-2xl overflow-hidden border-none outline-none">
          <div className="p-6 pb-4 border-b border-gray-100 relative">
            <div className="absolute top-5 right-5 z-10">
              <button
                onClick={() => setErrorDialogOpen(false)}
                className="p-2 bg-gray-100 border hover:bg-gray-200 rounded-xl transition-colors cursor-pointer text-gray-600"
              >
                <FiX size={18} />
              </button>
            </div>
            <ErrorDialogHeader>
              <ErrorDialogTitle className="text-xl font-bold text-gray-800">
                Error Details
              </ErrorDialogTitle>
              <ErrorDialogDescription className="text-gray-500">
                List of errors encountered for this order.
              </ErrorDialogDescription>
            </ErrorDialogHeader>
          </div>

          <div className="flex flex-col gap-3 p-6 max-h-[60vh] overflow-y-auto bg-gray-50/30">
            {Array.isArray(selectedErrors) ? (
              [...selectedErrors].reverse().map((err, index) => (
                <div
                  key={index}
                  className="p-4 bg-white border border-red-100 rounded-2xl shadow-sm"
                >
                  <div className="flex justify-between items-start mb-1">
                    <p className="font-bold text-red-800 text-sm bg-red-50 px-2 py-0.5 rounded-md">
                      {err.code}
                    </p>
                    <p className="text-xs text-gray-400 font-medium whitespace-nowrap ml-2">
                      {err.createdAt
                        ? format(
                            new Date(err.createdAt),
                            "dd MMM, yyyy 'at' h:mm a",
                          )
                        : "N/A"}
                    </p>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed mt-2">
                    {err.code === "PINCODE_NOT_SERVICEABLE" ||
                    err.code === "COURIER_NOT_AVAILABLE"
                      ? err.message.replace(
                          /Courier ([a-f0-9]+) not available/,
                          (match, id) =>
                            `Courier ${getCourierName(id)} not available`,
                        )
                      : err.message}
                  </p>
                </div>
              ))
            ) : (
              <div className="p-4 bg-white border border-red-100 rounded-2xl shadow-sm">
                <p className="text-red-600 text-sm">{String(selectedErrors)}</p>
              </div>
            )}
          </div>

          {/*  <div className="p-4 border-t border-gray-100 bg-white">
            <ErrorDialogFooter>
              <ErrorDialogCancel className="rounded-xl">
                Close
              </ErrorDialogCancel>
            </ErrorDialogFooter>
          </div> */}
        </ErrorDialogContent>
      </ErrorDialog>

      {/* Duplicate Order — ID confirmation dialog */}
      <DuplicateDialog
        open={duplicateDialogOpen}
        onOpenChange={setDuplicateDialogOpen}
      >
        <DuplicateDialogContent className="max-w-lg p-0 gap-0 md:rounded-3xl rounded-2xl overflow-hidden border-none outline-none">
          {/* Header */}
          <div className="p-6 pb-4 border-b border-secondary/10 bg-background relative">
            <button
              onClick={() => setDuplicateDialogOpen(false)}
              className="absolute top-5 right-5 p-2 bg-secondary/5 border border-secondary/15 hover:bg-secondary/10 rounded-xl transition-colors cursor-pointer text-secondary/60"
            >
              <FiX size={18} />
            </button>
            <DuplicateDialogHeader>
              <DuplicateDialogTitle className="text-xl font-bold text-secondary/90 font-nunito">
                Duplicate Order
              </DuplicateDialogTitle>
              <DuplicateDialogDescription className="text-secondary/55 text-sm font-ptsans">
                Review or edit the Order ID and Invoice Number before creating
                the duplicate.
              </DuplicateDialogDescription>
            </DuplicateDialogHeader>
          </div>

          {/* Fields */}
          <div className="flex flex-col gap-4 p-6 bg-background">
            <div className="flex flex-col gap-1">
              <label className="text-subtitle2 font-ptsans text-secondary/90 font-medium pl-2">
                Order ID
              </label>
              <input
                type="text"
                value={duplicateOrderId}
                onChange={(e) => setDuplicateOrderId(e.target.value)}
                className="w-full h-12 text-secondary/90 rounded-xl border-2 border-secondary/35 pl-4 pr-3 placeholder:text-secondary/50 py-3 text-body1 font-ptsans outline-none hover:border-secondary/60 focus:border-secondary/65 focus:outline-2 focus:outline-secondary/30 bg-transparent"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-subtitle2 font-ptsans text-secondary/90 font-medium pl-2">
                Invoice Number
              </label>
              <input
                type="text"
                value={duplicateInvoiceNo}
                onChange={(e) => setDuplicateInvoiceNo(e.target.value)}
                className="w-full h-12 text-secondary/90 rounded-xl border-2 border-secondary/35 pl-4 pr-3 placeholder:text-secondary/50 py-3 text-body1 font-ptsans outline-none hover:border-secondary/60 focus:border-secondary/65 focus:outline-2 focus:outline-secondary/30 bg-transparent"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-secondary/10 bg-background flex justify-end gap-2">
            <button
              onClick={() => setDuplicateDialogOpen(false)}
              className="px-4 py-2 h-10 rounded-xl text-sm font-medium font-ptsans text-secondary/70 border-2 border-secondary/25 hover:bg-secondary/5 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={confirmDuplicate}
              disabled={!duplicateOrderId.trim() || !duplicateInvoiceNo.trim()}
              className="px-4 py-2 h-10 rounded-xl text-sm font-medium font-ptsans bg-secondary/95 text-background hover:bg-secondary/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              Create Duplicate
            </button>
          </div>
        </DuplicateDialogContent>
      </DuplicateDialog>
      <Dialog type="warning" />
      <ConfirmationModal />
    </SpaceWrapper>
  );
};

export default BookedOrder;
